"use client";

import React from "react";
import { ShieldCheck, Calendar, Droplets, FileCheck, Award, ThumbsUp, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  icon: ShieldCheck,
  title: "Local Expertise",
  description: "Specialized in Cape Coral's unique canal systems, high humidity, and waterfront pest pressures."
}, {
  icon: Calendar,
  title: "Licensed & Insured",
  description: "FDACS Licensed Pest Control and Termite Operator with full insurance coverage for your protection."
}, {
  icon: Droplets,
  title: "Affordable Service Plans",
  description: "Monthly plans starting from $35/month for Cape Coral residents with flexible scheduling options."
}, {
  icon: FileCheck,
  title: "Reliable Local Service",
  description: "Locally owned and operated with guaranteed same-week appointments for all residents."
}];
const trustBadges = [{
  icon: Award,
  label: "Top Rated Local Expert"
}, {
  icon: ThumbsUp,
  label: "100% Satisfaction"
}];
export default function LocalCapeCoralPestControlExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="local-cape-coral-pest-control-expertise" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="overflow-hidden border-none shadow-xl bg-card">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {}
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center order-2 lg:order-1">
                <Badge variant="secondary" className="w-fit mb-6">
                  Local Expertise
                </Badge>
                
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default mb-6 text-card-foreground leading-tight">
                  Why Choose <span className="text-primary">Peace River Pest Control</span> in Cape Coral
                </h2>
                
                <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                  Living in the "Waterfront Wonderland" of Cape Coral offers unparalleled beauty, but the city's extensive 400-mile canal system creates a unique set of challenges for homeowners. The stagnant and brackish waters within residential canals serve as primary breeding grounds for various pest species that thrive in our subtropical climate. Along the Pine Island Sound corridor, salt-tolerant mosquito species are particularly aggressive, often migrating from the mangroves directly into Cape Coral neighborhoods. Beyond the water, the high moisture levels near canal banks significantly increase termite pressure. Subterranean termites are drawn to the damp soil foundations common in Cape Coral waterfront homes, where they can cause silent, structural damage if left unchecked. Our team specializes in the specific environmental variables of Cape Coral, understanding how the proximity to water influences pest behavior and lifecycle. Whether you are dealing with midges swarming your lanai or protecting your investment from wood-destroying organisms, we provide the localized expertise necessary to maintain a pest-free environment in Cape Coral. We monitor local weather patterns and seasonal shifts to ensure our treatments remain effective against the resilient pests that call Cape Coral home.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                  {features.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                      <div className="bg-primary/10 p-3 rounded-lg shrink-0 text-primary">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold font-default text-card-foreground mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {feature.description}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mt-auto">
                  <Button asChild size="lg" className="w-full sm:w-auto group">
                    <Link to="/ContactPage">
                      Get a Free Quote
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="/AboutUsPage">
                      Learn About Us
                    </Link>
                  </Button>
                </div>
              </div>

              {}
              <div className="relative min-h-[400px] lg:min-h-full order-1 lg:order-2 bg-muted">
                
                
                {}
                

                {}
                <div className="absolute bottom-6 left-6 right-6 flex flex-col sm:flex-row gap-4 justify-end">
                  {trustBadges.map((badge, i) => <Card key={i} data-index={i} className="bg-background/95 backdrop-blur-sm border-none shadow-lg">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="bg-primary p-2 rounded-full text-primary-foreground">
                          <badge.icon className="w-5 h-5" />
                        </div>
                        <span className="font-semibold font-default text-sm whitespace-nowrap">
                          {badge.label}
                        </span>
                      </CardContent>
                    </Card>)}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}